import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RatingStars, RatingInput } from "./RatingStars";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";

export function ReviewList({ reviews, emptyText = "No written reviews yet." }) {
  if (!reviews?.length) return <p className="text-sm text-ink-soft text-center py-4">{emptyText}</p>;
  return (
    <ul className="space-y-3" data-testid="review-list">
      {reviews.map((r) => (
        <li key={r.id} className="rounded-2xl bg-white border border-border px-4 py-3" data-testid={`review-${r.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            {r.user?.picture ? <img src={r.user.picture} alt="" className="w-7 h-7 rounded-full object-cover" /> : <span className="grid place-items-center w-7 h-7 rounded-full bg-secondary text-xs font-bold text-ink">{(r.user?.name || "M")[0]}</span>}
            <span className="text-sm font-bold text-ink">{r.user?.name}</span>
            <RatingStars value={r.value} size={13} className="ml-1" />
            <span className="ml-auto text-xs text-ink-soft">{new Date(r.updated_at || r.created_at).toLocaleDateString()}</span>
          </div>
          {r.review && <p className="mt-1.5 text-ink leading-relaxed break-words">{r.review}</p>}
        </li>
      ))}
    </ul>
  );
}

export function RateBox({ title, subtitle, postUrl, initialValue = 0, initialReview = "", disabled, disabledReason, onRated, testId = "rate-box" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [value, setValue] = useState(initialValue);
  const [review, setReview] = useState(initialReview || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(!!initialValue);

  const submit = async (v = value) => {
    if (!user) { toast("Log in to rate"); navigate("/login"); return; }
    if (!v) { toast.error("Pick a star rating first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(postUrl, { value: v, review });
      setDone(true);
      onRated?.(data);
      toast.success("Thanks for your rating!");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl bg-white border border-border shadow-soft p-6 sm:p-8 text-center" data-testid={testId}>
      <h3 className="font-heading text-2xl font-black text-ink">{title}</h3>
      <p className="text-ink-soft mt-1">{!user ? "Log in to leave a rating." : disabled ? disabledReason : subtitle}</p>
      <div className="mt-5 flex justify-center">
        <RatingInput value={value} disabled={disabled && !!user} onRate={(n) => { setValue(n); }} />
      </div>
      {user && !disabled && (
        <div className="mt-4 max-w-xl mx-auto">
          <textarea value={review} onChange={(e) => setReview(e.target.value)} rows={2} maxLength={1000} data-testid={`${testId}-review`}
            placeholder="Add a short review (optional)…"
            className="w-full rounded-xl border border-border px-4 py-2.5 text-base outline-none focus:border-coral focus:ring-4 focus:ring-coral/10 font-medium resize-none" />
          <button onClick={() => submit()} disabled={busy || !value} data-testid={`${testId}-submit`}
            className="mt-3 inline-flex items-center justify-center h-12 px-7 rounded-full bg-coral hover:bg-coral-hover text-white font-bold transition-colors disabled:opacity-50">
            {busy ? "Saving…" : done ? "Update rating" : "Submit rating"}
          </button>
        </div>
      )}
      {done && value > 0 && <p className="mt-3 text-sm font-bold text-leaf" data-testid={`${testId}-done`}>You rated this {value} ★</p>}
    </div>
  );
}
